import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ChevronUp,
  ChevronDown,
  KeyRound,
  LogOut,
  Wind,
  History,
} from "lucide-react";
import { 
  CpuIcon, 
  LayoutDashboard, 
  Dumbbell, 
  Eye, 
  UserPen 
} from "lucide-react";

const UserProfile = ({ username, email, role, onLogout, onResetPassword }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  return (
    <div className="absolute bottom-0 left-0 w-full border-t border-gray-200">
      <div
        className="p-4 hover:bg-gray-50 cursor-pointer"
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full bg-[#a32b13] flex items-center justify-center text-white font-semibold">
              {username.charAt(0)}
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900">
                {username}
              </h3>
              <p className="text-xs text-gray-500">{email}</p>
              {/* <p className="text-xs text-[#a32b13] font-medium">{role}</p> */}
            </div>
          </div>
          {isDropdownOpen ? (
            <ChevronUp className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          )}
        </div>
        {isDropdownOpen && (
          <div className="mt-2 py-1 border-t border-gray-100">
            <button
              onClick={onResetPassword}
              className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              <KeyRound className="w-4 h-4 mr-2" /> Reset Password
            </button>
            <button
              onClick={onLogout}
              className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default function PAGSidebar({ isSidebarOpen, setIsSidebarOpen }) {
  const sidebarRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredItems, setFilteredItems] = useState([]);
  const [openDropdown, setOpenDropdown] = useState("Real Time Data");
  const [user, setUser] = useState({
    username: "User",
    email: "user@example.com",
    role: "User",
  });
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);

  // Auth0 Management API configuration
  const config = {
    domain: "pag-optimus.us.auth0.com",
    clientId: "6PSiydOrLO17cBFQNVlWPRKGW6ky1BUN",
    audience: "https://pag-optimus.us.auth0.com/api/v2/",
  };

  // Fetch access token
  const getAccessToken = async () => {
    const response = await fetch(`https://${config.domain}/oauth/token`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        client_id: config.clientId,
        client_secret: "j_yvFASXENX0Y785_Kbd7RCZbFbGi5qFapXs8tN0CQvBrk9-I8DYJ_KBnZZu6BeH",
        audience: config.audience,
        grant_type: "client_credentials",
        scope: "read:users read:users_app_metadata update:users_app_metadata read:user_idp_tokens read:client_grants create:users update:users delete:users read:clients read:client_credentials create:client_credentials",
      }),
    });
    const data = await response.json();
    return data.access_token;
  };

  const [menuItems, setMenuItems] = useState([]);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    if (storedUser) {
      setUser(storedUser);
      updateMenuItems(storedUser.role);
    } else {
      setUser(null);
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("access_token");
    setUser(null);
    setMenuItems([]);
    navigate("/login", { replace: true });
  };

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const token = await getAccessToken();
        const response = await fetch(`${config.audience}users`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.ok) {
          const data = await response.json();
          const storedUser = JSON.parse(localStorage.getItem("user"));

          const auth0User = data.find((u) => u.email === storedUser?.email);

          if (auth0User) {
            const userData = {
              username: auth0User.user_metadata?.username || auth0User.email.split("@")[0],
              email: auth0User.email,
              role: storedUser.role || "User",
            };

            setUser(userData);
            localStorage.setItem("user", JSON.stringify(userData));

            console.log("Fetched User Role:", userData.role);
            updateMenuItems(userData.role);
          }
        } else {
          console.error("Failed to fetch users:", await response.text());
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    fetchCurrentUser();
  }, []);

  const updateMenuItems = (role) => {
    const baseMenuItems = [
      {
        name: "Real Time Data",
        alwaysOpen: true,
        children: [
          {
            name: "Dashboard",
            path: "/dashboard",
            icon: <LayoutDashboard className="text-gray-600" size={21} />,
          },
          {
            name: "Gym", 
            path: "/gym",
            icon: <Dumbbell className="text-gray-600" size={21} />,
          },
          {
            name: "Air Quality",
            path: "/iaq",
            icon: <Wind className="text-gray-600" size={21} />,
          },
        ],
      },
      // Only show History & Trends for Admin and Global Admin
      ...(role !== "User" ? [{
        name: "History & Trends",
        children: [
          {
            name: "Historical Data",
            path: "/historical",
            icon: <History className="text-gray-600" size={21} />,
          },
        ],
      }] : []),
      // Only show Concierge for Admin and Global Admin
      ...(role !== "User" ? [{
        name: "Concierge",
        path: "/concierge", 
        icon: <Eye className="text-gray-600" size={21} />,
      }] : []),
      // Floor Plan - available to all users
      {
        name: "Floor Plan",
        path: "/floorplan", 
        icon: (
          <svg
            className="w-5 h-5 text-gray-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
            />
          </svg>
        ),
      },
    ];

    // Settings section with role-based access
    const settingsChildren = [
      {
        name: "Manage Devices",
        path: "/devices",
        icon: <CpuIcon className="text-gray-600" size={21} />,
      },
    ];

    // Add Access Roles for Admin and Global Admin
    if (role === "Admin" || role === "Global Admin") {
      settingsChildren.push({
        name: "Access Roles",
        path: "/roles",
        icon: <UserPen className="text-gray-600" size={21} />,
      });
    }

    const settingsSection = {
      name: "Settings",
      children: settingsChildren,
    };

// Only add Settings section for Admin and Global Admin
const updatedMenuItems = role === "User" 
  ? baseMenuItems 
  : [...baseMenuItems, settingsSection];    
    setMenuItems(updatedMenuItems);
    console.log("Updated Menu Items for role", role, ":", updatedMenuItems);
  };

  // Helper function to check if a path is active
  const isPathActive = (itemPath, childPath = null) => {
    const currentPath = location.pathname;
    
    if (childPath) {
      return currentPath === childPath;
    }
    
    if (itemPath === "/dashboard" && (currentPath === "/" || currentPath === "/dashboard")) {
      return true;
    }
    
    return currentPath === itemPath;
  };

  // Helper function to check if any child is active (for parent highlighting)
  const hasActiveChild = (children) => {
    if (!children) return false;
    return children.some(child => isPathActive(null, child.path));
  };

  // Close sidebar if clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setIsSidebarOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [setIsSidebarOpen]);

  // Auto-expand Real Time Data section
  useEffect(() => {
    const currentPath = location.pathname;
    const realTimeDataPaths = ["/iaq", "/dashboard", "/gym"];
    const historyTrendsPaths = ["/historical"];
    
    if (realTimeDataPaths.includes(currentPath)) {
      setOpenDropdown("Real Time Data");
    } else if (historyTrendsPaths.includes(currentPath)) {
      setOpenDropdown("History & Trends");
    }
  }, [location.pathname]);

  // Filter menu items based on search query
  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredItems(menuItems);
    } else {
      const filtered = menuItems
        .map((item) => {
          const matchingChildren = item.children?.filter((child) =>
            child.name.toLowerCase().includes(searchQuery.toLowerCase())
          ) || [];

          if (
            matchingChildren.length > 0 ||
            item.name.toLowerCase().includes(searchQuery.toLowerCase())
          ) {
            return { ...item, children: matchingChildren };
          }

          return null;
        })
        .filter(Boolean);

      setFilteredItems(filtered);

      if (filtered.length > 0) {
        setOpenDropdown(filtered[0].name);
      }
    }
  }, [searchQuery, menuItems]);

  // Handle "Cmd + K" to focus the search bar
  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        
        // Open sidebar if not already open
        if (!isSidebarOpen) {
          setIsSidebarOpen(true);
        }
        
        // Focus and select all text in search bar with a small delay
        setTimeout(() => {
          const searchBar = document.getElementById("search-bar");
          if (searchBar) {
            searchBar.focus();
            searchBar.select(); 
          }
        }, 100);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isSidebarOpen, setIsSidebarOpen]);

  if (!user) return <div>Loading...</div>;

  const handleNavigate = (path, isChild = false) => {
    if (path !== "#") {
      const currentPath = location.pathname;
      
      const isCurrentlyActive = isChild ? 
        currentPath === path :
        (path === "/dashboard" && (currentPath === "/" || currentPath === "/dashboard")) ||
        currentPath === path;
      
      if (isCurrentlyActive) {
        setIsSidebarOpen(false);
      } else {
        navigate(path);
      }
    }
  };

  const sendPasswordResetEmail = async () => {
    try {
      const response = await fetch(
        "https://pag-optimus.us.auth0.com/dbconnections/change_password",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            client_id: "6PSiydOrLO17cBFQNVlWPRKGW6ky1BUN",
            email: user.email,
            connection: "Username-Password-Authentication",
          }),
        }
      );

      if (response.ok) {
        setIsOverlayOpen(true);
        setTimeout(() => setIsOverlayOpen(false), 3000);
      } else {
        const errorData = await response.json();
        alert(errorData.error_description || "Failed to send reset email.");
      }
    } catch (error) {
      console.error("Error sending password reset email:", error);
      alert("Something went wrong. Please try again.");
    }
  };

  return (
    <div>
      <div
        className={`fixed inset-0 bg-black bg-opacity-40 transition-opacity duration-200 ${
          isSidebarOpen ? "opacity-100 visible z-20" : "opacity-0 invisible"
        }`}
      ></div>
      <div
        ref={sidebarRef}
        className={`fixed top-0 left-0 h-full bg-white shadow-lg w-[366px] transition-transform duration-300 transform ${
          isSidebarOpen ? "translate-x-0" : "-translate-x-full"
        } z-30`}
      >
        <div className="flex items-center justify-between pt-3 pl-4 pb-2 pr-4">
          <div className="flex items-center">
            <img 
              src="/PAG.png" 
              alt="PAG Logo" 
              className="h-12" 
              onError={(e) => {
                e.target.style.display = 'none';
              }} 
            />
          </div>
          <button
            onClick={() => setIsSidebarOpen(false)}
            className="w-14 pt-4 h-8 flex items-center justify-center font-semibold text-gray-500 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        {/* Search Bar */}
        <div className="relative p-4 mb-3">
          <input
            id="search-bar"
            type="text"
            placeholder="Search"
            className="w-[96%] pl-10 pr-4 py-2 border border-[#a32b13a5] text-gray-600 bg-white rounded-lg focus:outline-none focus:ring-1 focus:ring-[#a32b13a5]"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <span className="absolute left-7 top-1/2 transform -translate-y-1/2 text-gray-400">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              x="0px"
              y="0px"
              width="20"
              height="20"
              viewBox="0,0,256,256"
            >
              <g
                fill="#4d4d4d"
                fillRule="nonzero"
                stroke="none"
                strokeWidth="1"
                strokeLinecap="butt"
                strokeLinejoin="miter"
                strokeMiterlimit="10"
                strokeDasharray=""
                strokeDashoffset="0"
                fontFamily="none"
                fontWeight="none"
                fontSize="none"
                textAnchor="none"
              >
                <g transform="scale(8.53333,8.53333)">
                  <path d="M13,3c-5.511,0 -10,4.489 -10,10c0,5.511 4.489,10 10,10c2.39651,0 4.59738,-0.85101 6.32227,-2.26367l5.9707,5.9707c0.25082,0.26124 0.62327,0.36648 0.97371,0.27512c0.35044,-0.09136 0.62411,-0.36503 0.71547,-0.71547c0.09136,-0.35044 -0.01388,-0.72289 -0.27512,-0.97371l-5.9707,-5.9707c1.41266,-1.72488 2.26367,-3.92576 2.26367,-6.32227c0,-5.511 -4.489,-10 -10,-10zM13,5c4.43012,0 8,3.56988 8,8c0,4.43012 -3.56988,8 -8,8c-4.43012,0 -8,-3.56988 -8,-8c0,-4.43012 3.56988,-8 8,-8z"></path>
                </g>
              </g>
            </svg>
          </span>
          <span className="absolute right-[68px] top-1/2 transform -translate-y-1/2 border border-[#a32b13a5] text-[#a32b13a5] text-xs font-semibold px-2 py-1 rounded mr-1">
            ⌘
          </span>
          <span className="absolute right-10 top-1/2 transform -translate-y-1/2 border border-[#a32b13a5] text-[#a32b13a5] font-medium text-xs px-2 py-1 rounded">
            K
          </span>
        </div>

        <ul>
          {filteredItems.map((item, index) => {
            const isActive = hasActiveChild(item.children);
            
            return (
              <li key={index} className="mb-3">
                {item.children ? (
                  // Render dropdown for items with children
                  <>
                    <button
                      className={`
                        flex items-center justify-between w-full px-4 py-2 text-left font-semibold
                        ${
                          isActive || openDropdown === item.name
                            ? "bg-gray-200 text-gray-700 "
                            : "text-gray-700 hover:bg-gray-100"
                        }
                        transition
                      `}
                      onClick={() =>
                        setOpenDropdown(openDropdown === item.name ? "" : item.name)
                      }
                    >
                      <span>{item.name}</span>
                      {openDropdown === item.name ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </button>
                    {openDropdown === item.name && (
                      <ul className="pl-6 mt-2">
                        {item.children.map((child, childIndex) => {
                          const isChildActive = isPathActive(null, child.path);
                          
                          return (
                            <li key={childIndex} className="mb-2 mt-2">
                              <button
                                className={`
                                  w-full flex items-center px-3 py-2 text-left rounded-md transition
                                  ${
                                    isChildActive
                                      ? "bg-gray-100 text-black font-normal text-sm"
                                      : "text-black hover:bg-gray-100 font-normal text-sm"
                                  }
                                  ${child.disabled ? "text-gray-400 cursor-not-allowed" : ""}
                                `}
                                onClick={() => !child.disabled && handleNavigate(child.path, true)}
                                disabled={child.disabled}
                              >
                                <span className="mr-3">{child.icon}</span>
                                {child.name}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </>
                ) : (
                  // Single menu item
                  <button
                    className={`
                      w-full flex items-center px-4 py-2 text-left font-semibold rounded-md transition
                      ${
                        isPathActive(null, item.path)
                          ? "bg-gray-200 text-gray-700"
                          : "text-gray-700 hover:bg-gray-100"
                      }
                    `}
                    onClick={() => handleNavigate(item.path, false)}
                  >
                    <span className="mr-3">{item.icon}</span>
                    {item.name}
                  </button>
                )}
              </li>
            );
          })}
        </ul>

        <UserProfile
          username={user.username}
          email={user.email}
          role={user.role}
          onLogout={handleLogout}
          onResetPassword={sendPasswordResetEmail}
        />

        {isOverlayOpen && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white p-4 rounded-lg shadow-lg text-center w-64">
              <h2 className="text-[16px] font-semibold text-gray-900">
                Password Reset Sent
              </h2>
              <p className="text-gray-600 text-sm mt-2">
                A reset link has been sent to <strong>{user.email}</strong>.
                Please check your inbox.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}